import { Component, Inject } from "@angular/core";
import { FormControl, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Observable, zip } from "rxjs";
import { REGULAR_EXPRESSION, TenantConfig } from "../../app.constants";
import { ApplicationError } from "../../_errors/application-error";
import { InspectionReportNew } from "../../_objects/inspection-report";
import { ReportTemplateListItem } from "../../_offline/models/report-preview/report-template";
import { ConfigurationItem } from "../../_objects/tenant-config";
import { CommonSnackBarService } from "../../_services/common-snack-bar.service";
import { InspectionReportService } from "../../_services/http-api/inspection-report.service";
import { TenantConfigService } from "../../_services/http-api/tenant-config.service";
import { SpinnerService } from "../../_services/spinner.service";
import { ReportTemplateService } from "../../_services/http-api/report-template.service";
import { CustomValidators } from "../../_shared/custom-validator";
import { UtilsService } from "../../_services/_utils.service";
import { MeUserService } from "../../_services/http-api/me-user.service";
import { NetworkStatusService } from "../../_services/network-status.service";
import { Router } from "@angular/router";
import { RoutePage, RouteSettings } from "../../app-routes.enum";

@Component({
  selector: "app-inspection-report-confirmation-dialog",
  templateUrl: "./inspection-report-confirmation-dialog.component.html",
  styleUrls: ["./inspection-report-confirmation-dialog.component.scss"],
  standalone: false
})
export class InspectionReportConfirmationDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<InspectionReportConfirmationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}
}

@Component({
  selector: "app-inspection-report-add-dialog",
  templateUrl: "./inspection-report-add-dialog.component.html",
  styleUrls: ["./inspection-report-add-dialog.component.scss"],
  standalone: false
})
export class InspectionReportAddDialogComponent {
  reportTitleMaxLength = 100;
  regularExpression = this.utilsService.CombiningRegExp([
    "^",
    REGULAR_EXPRESSION.RESERVED_WORDS_PATTERN,
    "[",
    REGULAR_EXPRESSION.HALF_ALPHANUMERIC_PATTERN,
    REGULAR_EXPRESSION.KANJI_PATTERN,
    REGULAR_EXPRESSION.SYMBOL_PATTERN,
    "]+$"
  ]);
  reportTitleControl = new FormControl<string>("", [
    CustomValidators.requiredTrimmed(),
    Validators.maxLength(this.reportTitleMaxLength),
    Validators.pattern(this.regularExpression)
  ]);
  reportTemplateControl = new FormControl<string>("", [CustomValidators.requiredTrimmed()]);
  templatesMaster: ReportTemplateListItem[];
  reportTemplate: ReportTemplateListItem;
  isProcess = false;

  // report title自動入力モード
  tenantConfigs: ConfigurationItem[];
  observableResponse: Observable<any> = new Observable<{}>();
  isLoading = true;

  constructor(
    private router: Router,
    private meUserService: MeUserService,
    private snackBar: CommonSnackBarService,
    private inspectionReportService: InspectionReportService,
    private reportTemplateService: ReportTemplateService,
    private dialog: MatDialog,
    private oscSpinnerService: SpinnerService,
    public dialogRef: MatDialogRef<InspectionReportAddDialogComponent>,
    public tenantConfigService: TenantConfigService,
    private networkStatusService: NetworkStatusService,
    public utilsService: UtilsService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.oscSpinnerService.show(true);

    zip(this.getReportTemplatesBySubjectModelId(data.subjectModelId), this.tenantConfigService.getTenantConfigs()).subscribe(
      ([templates, config]) => {
        this.templatesMaster = templates;
        this.tenantConfigs = config;
        this.tenantConfigService.setTenantConfig(config);
      },
      (error) => {
        console.log(error);
        this.snackBar.open("An error occurred during load template.");
      },
      () => {
        this.oscSpinnerService.hide(true);
        this.isLoading = false;
        if (this.templatesMaster.length > 0) {
          this.reportTemplateControl.setValue(this.templatesMaster[0].id);
          this.reportTemplate = this.templatesMaster[0];
          this.reportTitleControl.setValue(null);
          this.reportTitleControl.enable();
        } else {
          this.reportTemplateControl.disable();
          this.reportTitleControl.disable();
        }
      }
    );
  }

  loadTemplateId() {
    if (this.reportTemplateControl.value) {
      this.templatesMaster.forEach((template) => {
        if (template.id === this.reportTemplateControl.value) {
          this.reportTemplate = template;
          this.reportTitleControl.setValue(null);
          this.reportTitleControl.enable();
        }
      });
    }
  }

  getReportTemplatesBySubjectModelId(subjectModelId: string): Observable<ReportTemplateListItem[]> {
    if (!subjectModelId) {
      throw new ApplicationError("SubjectModel is undefined.");
    }
    return this.reportTemplateService.getReportTemplates(subjectModelId);
  }

  save(): void {
    const imageSelectConfig = this.tenantConfigs.find((_) => _.key === TenantConfig.ImageSelectModeWhenCreatingReport.KEY);

    switch (imageSelectConfig?.value) {
      case TenantConfig.ImageSelectModeWhenCreatingReport.FALSE:
        // 全て画像を含めない
        this.submit(false);
        break;
      case TenantConfig.ImageSelectModeWhenCreatingReport.TRUE:
        // 全て画像を含める
        this.submit(true);
        break;
      case TenantConfig.ImageSelectModeWhenCreatingReport.OPTIONAL:
      default:
        // 全て画像を含めるかの確認ダイアログを出す
        this.openConfirmationDialog();
        break;
    }
  }

  openConfirmationDialog() {
    const dialogRef = this.dialog.open(InspectionReportConfirmationDialogComponent, {
      width: "600px",
      height: "235px",
      autoFocus: false
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result !== undefined) {
        this.submit(result);
      }
    });
  }

  openReportTemplate() {
    if (this.networkStatusService.isOnPremise) {
      this.router.navigate([RoutePage.Settings, RouteSettings.OfflineReportTemplate]);
    } else {
      window.open(this.meUserService.getReportTemplateBuilderEndpoint(["template-list"], "?application=ViSOL"), "_blank");
    }
  }

  submit(isSelectedImage: boolean) {
    this.isProcess = true;
    this.oscSpinnerService.show(true);
    const inspectionReportNew: InspectionReportNew = new InspectionReportNew();
    inspectionReportNew.inspectionIds = this.data.inspectionIds;
    inspectionReportNew.templateId = this.reportTemplateControl.value;
    inspectionReportNew.isSelectedImage = isSelectedImage;
    inspectionReportNew.title = this.reportTitleControl.value.trim();
    inspectionReportNew.reportLocations = [];
    inspectionReportNew.schemaVersion = "1";

    this.observableResponse = this.inspectionReportService.createInspectionReport(inspectionReportNew);
    let resReport;
    this.observableResponse.subscribe(
      (data) => {
        resReport = data;
      },
      (error) => {
        this.snackBar.open("An error occurred during report setting.");
        console.log(error);
        this.isProcess = false;
        this.oscSpinnerService.hide(false);
      },
      () => {
        this.snackBar.open("Report setting completed!");
        this.dialogRef.close(resReport);
        this.isProcess = false;
        this.oscSpinnerService.hide(false);
      }
    );
  }
}
